import React from 'react';

/**
 * FilterSidebar — 리스트 페이지 좌측 필터 사이드바 (lg+ 전용).
 *
 * 시안: wireframes/concept-a-warm/list.html .r-list-sidebar (lg+)
 *
 * 모바일: 부모 ListLayout이 자동으로 hidden 처리.
 *        대신 모바일 sticky FilterChipRow가 동일 필터를 가로 chip으로 표시.
 *
 * 데스크탑 (lg 1024+):
 *   - 280px 폭 sticky 좌측 패널
 *   - 카테고리 그룹별 11px uppercase 라벨
 *   - 각 그룹 안에 세로 리스트 (선택 시 accent border + bg-frame)
 *
 * 컴포넌트는 일반적인 그룹 API를 제공하고, 호출부(ListPage)가 데이터·핸들러를 전달.
 */

export interface FilterSidebarOption {
  /** 옵션 식별자 (예: 'sub60', '동구', 'this_year') */
  key: string;
  /** 표시 라벨 (예: '~ 59㎡', '동구') */
  label: string;
  /** 우측 카운트 표시 (선택) */
  count?: number;
}

export interface FilterSidebarGroup {
  /** 그룹 제목 (예: '지역', '면적') */
  title: string;
  options: FilterSidebarOption[];
  /** 현재 선택된 옵션 key (빈 문자열 = "전체") */
  selected: string;
  /** 옵션 선택 시 호출 (빈 문자열 = 전체 해제) */
  onSelect: (key: string) => void;
  /** 전체 옵션 라벨 (기본 "전체") */
  allLabel?: string;
}

export interface FilterSidebarProps {
  groups: FilterSidebarGroup[];
  /** 사이드바 최상단 콘텐츠 (예: 검색바, 결과 카운트) */
  header?: React.ReactNode;
  className?: string;
}

// v2 목업 `.opt` — 체크 박스(16px, 선택 시 오렌지) + 라벨 + 카운트 (2026-09-21 배치 4)
const FilterRow: React.FC<{
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}> = ({ label, count, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`flex w-full items-center gap-2 py-[5px] text-left text-[13px] transition-colors ${
      active ? 'font-semibold text-ink' : 'text-body hover:text-ink'
    }`}
  >
    <i
      aria-hidden
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border-[1.5px] not-italic ${
        active ? 'border-accent bg-accent text-white' : 'border-ca-line bg-frame'
      }`}
    >
      {active && (
        <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={4} strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </i>
    <span className="flex-1">{label}</span>
    {typeof count === 'number' && (
      <span className="num text-[11.5px] font-medium text-mute2">{count.toLocaleString()}</span>
    )}
  </button>
);

export const FilterSidebar: React.FC<FilterSidebarProps> = ({
  groups,
  header,
  className = '',
}) => (
  <div className={`flex flex-col ${className}`}>
    {header}
    {groups.map((group) => (
      <div key={group.title} className="flex flex-col border-t border-ca-line py-3.5 first:border-t-0">
        <h4 className="mb-2 text-[12px] font-bold text-muted">
          {group.title}
        </h4>
        <FilterRow
          label={group.allLabel ?? '전체'}
          active={group.selected === ''}
          onClick={() => group.onSelect('')}
        />
        {group.options.map((opt) => (
          <FilterRow
            key={opt.key}
            label={opt.label}
            count={opt.count}
            active={group.selected === opt.key}
            onClick={() => group.onSelect(opt.key)}
          />
        ))}
      </div>
    ))}
  </div>
);

export default FilterSidebar;
